import { AnyAction, Dispatch } from 'redux';
import { axiosAzureAPI } from '../../../services/axiosService';
import { AppState } from '../../../store/appState';
import { v4 as uuid } from 'uuid';
import { setFileTooLarge, setSubmitting } from './reportSlice';
import { ReportSpecificSalesforceData } from './reportConfigs';
import { openMessageModal } from '../../../components/statusModal/messageModalActionCreators';
import { useHistory } from 'react-router-dom';
import { AxiosError } from 'axios';
import axios from 'axios';

// Types for presigned upload
export interface PresignedUploadFile {
    filename: string;
    contentType?: string;
}

export interface PresignedUploadResponse {
    success: boolean;
    files: Array<{
        filename: string;
        uploadUrl: string;
        blobUrl: string;
        blobPath: string;
        expiresAt: string;
        requiredHeaders: Record<string, string>;
    }>;
    totalFiles: number;
}

// Action creator for getting presigned upload URLs
export const getPresignedUploadUrls = async (files: PresignedUploadFile[]): Promise<PresignedUploadResponse> => {
    try {
        const payload = { files };
        
        const response = await axiosAzureAPI.post('/api/salesforce/presigned-upload-url', payload);
        return response.data as PresignedUploadResponse;
    } catch (error) {
        console.error('Error getting presigned upload URLs:', error);
        throw error;
    }
};

// Helper function to upload file to Azure Storage using presigned URL
const uploadFileToAzure = async (file: File, uploadUrl: string, requiredHeaders: Record<string, string>) => {
    try {
        await axios.put(uploadUrl, file, {
            headers: {
                ...requiredHeaders,
                'Content-Type': file.type || 'application/octet-stream',
                'x-ms-blob-type': 'BlockBlob',
            },
        });
        return true;
    } catch (error) {
        console.error('Error uploading file to Azure:', error);
        return false;
    }
};

// Enhanced submit report with presigned uploads
export const submitReportWithPresignedUpload = (
    files: File[], 
    reportSpecificSalesforceData: ReportSpecificSalesforceData, 
    activityId: string, 
    activityLanguageKey: string, 
    history: ReturnType<typeof useHistory>
) => async (dispatch: Dispatch, getState: () => AppState) => {
    try {
        dispatch(setSubmitting(true));
        const state = getState();
        const reportState = state.report;
        const reportId = uuid();

        const description = reportState.description;
        let { extraDescription, ...restOfSpecificSalesforceData } = reportSpecificSalesforceData;
        if(reportState.rating && reportState.rating !== 0) {
            extraDescription = "Rating: " + reportState.rating.toString() + "/5";
        }

        const payload = {
            id: reportId,
            address: reportState.address,
            activityId,
            activityLanguageKey,
            description,
            extraDescription,
            ...restOfSpecificSalesforceData,
        };

        let blobUrls: string[] = [];

        // Handle file uploads if there are files
        if (files && files.length > 0 && !reportState.fileTooLarge) {
            // Check total file size
            const totalFileSize = files.reduce((total, file) => total + file.size, 0);
            if (totalFileSize > 10485760) { // 10MB limit
                dispatch(setFileTooLarge(true));
                dispatch(setSubmitting(false));
                return;
            }

            // Prepare files for presigned URL request
            const presignedFiles: PresignedUploadFile[] = files.map(file => ({
                filename: file.name,
                contentType: file.type || 'application/octet-stream',
            }));

            // Get presigned upload URLs
            const presignedResponse = await getPresignedUploadUrls(presignedFiles);
            
            if (!presignedResponse.success) {
                throw new Error('Failed to get presigned upload URLs');
            }

            // Upload files to Azure Storage
            const uploadPromises = files.map(async (file, index) => {
                const presignedData = presignedResponse.files[index];
                const success = await uploadFileToAzure(file, presignedData.uploadUrl, presignedData.requiredHeaders);
                if (success) {
                    return presignedData.blobUrl;
                } else {
                    throw new Error(`Failed to upload file: ${file.name}`);
                }
            });

            blobUrls = await Promise.all(uploadPromises);
        }

        // Submit the report with blob URLs
        const reportPayload = {
            reportData: JSON.stringify(payload),
            blobUrls: blobUrls,
        };

        await axiosAzureAPI.post('/api/salesforce/save-311', reportPayload);
        
        // Show success message
        dispatch(openMessageModal('success-submit-case', 'success') as unknown as AnyAction);
        
        dispatch(setSubmitting(false));
        history.push('/dashboard');
        
    } catch (err) {
        console.error(err);
        if ((err as AxiosError)?.response?.status === 413) {
            console.error('File Upload too Large');
            dispatch(setFileTooLarge(true));
        } else {
            dispatch(openMessageModal(null, 'error') as unknown as AnyAction);
        }
        dispatch(setSubmitting(false));
    }
};

// Original submit report function (kept for backward compatibility)
export const submitReport = (files: any[], reportSpecificSalesforceData: ReportSpecificSalesforceData, activityId: string, activityLanguageKey: string, history: ReturnType<typeof useHistory>) => async (dispatch: Dispatch, getState: () => AppState) => {
    try {
        dispatch(setSubmitting(true));
        const state = getState();

        const reportState = state.report;
        const reportId = uuid();

        const description = reportState.description;

        let { extraDescription, ...restOfSpecificSalesforceData } = reportSpecificSalesforceData;
        if(reportState.rating && reportState.rating !== 0) extraDescription = "Rating: " + reportState.rating.toString() + "/5";

        const payload = {
            id: reportId,
            address: reportState.address,
            activityId,
            activityLanguageKey,
            description,
            extraDescription,
            ...restOfSpecificSalesforceData,
        };

        const formData = new FormData();

        formData.append("reportData", JSON.stringify(payload));

        let fileCounter = 1;
        let fileSize = 0;
        if (!reportState.fileTooLarge) {
            for (const file of files) {
                formData.append(`file-${fileCounter}-${file.name}`, file);
                fileCounter += 1;
                fileSize += file.size;
            }

            if (fileSize > 10485760) {
                dispatch(setFileTooLarge(true));
            }
        }

        await axiosAzureAPI.post('/api/salesforce/save-311', formData);

        // Ensure the user sees the success message for their activity
        dispatch(openMessageModal('success-submit-case', 'success') as unknown as AnyAction);
        dispatch(setSubmitting(false));
        history.push('/dashboard');
    } catch (err) {
        console.error(err);
        if ((err as AxiosError)?.response?.status === 413) {
            console.error('File Upload too Large');
            dispatch(setFileTooLarge(true));
        } else {
            dispatch(openMessageModal(null, 'error') as unknown as AnyAction);
        }
        dispatch(setSubmitting(false));
    }
};

// Submit report with already uploaded blob URLs
export const submitReportWithBlobUrls = (
    blobUrls: string[],
    reportSpecificSalesforceData: ReportSpecificSalesforceData, 
    activityId: string, 
    activityLanguageKey: string, 
    history: ReturnType<typeof useHistory>
) => async (dispatch: Dispatch, getState: () => AppState) => {
    try {
        dispatch(setSubmitting(true));
        const state = getState();
        const reportState = state.report;
        const reportId = uuid();

        const description = reportState.description;
        let { extraDescription, ...restOfSpecificSalesforceData } = reportSpecificSalesforceData;
        if(reportState.rating && reportState.rating !== 0) {
            extraDescription = "Rating: " + reportState.rating.toString() + "/5";
        }

        const payload = {
            id: reportId,
            address: reportState.address,
            activityId,
            activityLanguageKey,
            description,
            extraDescription,
            ...restOfSpecificSalesforceData,
        };

        // Submit the report with blob URLs
        const reportPayload = {
            reportData: JSON.stringify(payload),
            blobUrls: blobUrls,
        };

        await axiosAzureAPI.post('/api/salesforce/save-311', reportPayload);
        
        // Show success message
        dispatch(openMessageModal('success-submit-case', 'success') as unknown as AnyAction);
        
        dispatch(setSubmitting(false));
        history.push('/dashboard');
        
    } catch (err) {
        console.error(err);
        if ((err as AxiosError)?.response?.status === 413) {
            console.error('File Upload too Large');
            dispatch(setFileTooLarge(true));
        } else {
            dispatch(openMessageModal(null, 'error') as unknown as AnyAction);
        }
        dispatch(setSubmitting(false));
    }
};
